import { Dispatch } from 'redux';
import {
  RecordActionTypes,
  CurrentUserInfo,
  YogaRecord,
  HomeVisitRecord,
  InterestCheckRecord,
  MCITestRecord,
  LifeCheckRecord,
  BarthelRecord,
  MoffTrainingRecord,
  TRAINING_MENUS,
} from '../../constants/Records';
import { YogaApiClient } from './api/yoga';
import { HomeVisitApiClient } from './api/homevisit';
import { InterestApiClient } from './api/interest';
import { MCIApiClient } from './api/mci';
import { LifeCheckApiClient } from './api/lifecheck';
import { BarthelApiClient } from './api/barthel';
import { TrainingLogApiClient } from '../TrainingLog/api';

// 404エラーを空配列として処理するヘルパー
const handleNotFound = (error: any) => {
  if (error.response?.status === 404) {
    return [];
  }
  throw error;
};

// 現在のユーザーを設定
export const setCurrentUser = (userInfo: CurrentUserInfo) => {
  return {
    type: RecordActionTypes.SET_CURRENT_USER,
    payload: userInfo,
  };
};

// Stateをリセット
export const resetRecords = () => {
  return {
    type: RecordActionTypes.RESET,
  };
};

// レコード件数を取得（各APIを並列で呼び出し）
export const fetchAllRecordCounts = (uniqueId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.FETCH_ALL_COUNTS_START });

    try {
      const [yogaClient, homevisitClient, interestClient, mciClient, lifecheckClient, barthelClient] =
        await Promise.all([
          YogaApiClient.ClientFactory(),
          HomeVisitApiClient.ClientFactory(),
          InterestApiClient.ClientFactory(),
          MCIApiClient.ClientFactory(),
          LifeCheckApiClient.ClientFactory(),
          BarthelApiClient.ClientFactory(),
        ]);

      // 件数取得用に効率的なAPIを使用
      const [yogaCount, homevisitCount, interestCount, mciCount, lifecheckRecords, barthelRecords] = await Promise.all([
        yogaClient.getRecordCount(uniqueId).catch(() => 0),
        homevisitClient.getRecordCount(uniqueId).catch(() => 0),
        interestClient.getRecordCount(uniqueId).catch(() => 0),
        mciClient.getScoreCount(uniqueId).catch(() => 0),
        lifecheckClient.getRecords(uniqueId).catch(handleNotFound),
        barthelClient.getRecordsByDateRange(uniqueId, '2000-01-01', '2099-12-31').catch(handleNotFound),
      ]);

      const counts = {
        moffcheck: 0, // TODO: 既存Check APIから取得
        mofftraining: 0, // モフトレの件数はサマリーAPIで取得するため、ここでは0
        yoga: yogaCount,
        homevisit: homevisitCount,
        interest: interestCount,
        mci: mciCount,
        lifecheck: lifecheckRecords.length,
        barthel: barthelRecords.length,
      };

      dispatch({
        type: RecordActionTypes.FETCH_ALL_COUNTS_FINISH,
        payload: counts,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

// ========================================
// Yoga関連アクション
// ========================================

export const resetYogaFlags = () => ({
  type: RecordActionTypes.YOGA_RESET_FLAGS,
});

export const fetchYogaRecords = (uniqueId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.YOGA_START_FETCH });

    try {
      const client = await YogaApiClient.ClientFactory();
      const recordsPromise = client.getRecords(uniqueId).catch(handleNotFound);
      const [records, menus] = await Promise.all([recordsPromise, client.getMenus()]);

      dispatch({
        type: RecordActionTypes.YOGA_FINISH_FETCH,
        payload: { records, menus },
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const fetchYogaRecord = (uniqueId: string, recordTime: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.YOGA_START_FETCH_ONE });

    try {
      const client = await YogaApiClient.ClientFactory();
      const record = await client.getRecord(uniqueId, recordTime);

      dispatch({
        type: RecordActionTypes.YOGA_FINISH_FETCH_ONE,
        payload: record,
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({
          type: RecordActionTypes.YOGA_FINISH_FETCH_ONE,
          payload: null,
        });
      } else {
        dispatch({
          type: RecordActionTypes.API_ERROR,
          error,
        });
      }
    }
  };
};

export const createYogaRecord = (data: Partial<YogaRecord>) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.YOGA_START_CREATE });

    try {
      const client = await YogaApiClient.ClientFactory();
      const record = await client.createRecord(data);

      dispatch({
        type: RecordActionTypes.YOGA_FINISH_CREATE,
        payload: record,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const updateYogaRecord = (uniqueId: string, recordTime: string, data: Partial<YogaRecord>) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.YOGA_START_UPDATE });

    try {
      const client = await YogaApiClient.ClientFactory();
      const record = await client.updateRecord(uniqueId, recordTime, data);

      dispatch({
        type: RecordActionTypes.YOGA_FINISH_UPDATE,
        payload: record,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const deleteYogaRecord = (uniqueId: string, recordTime: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.YOGA_START_DELETE });

    try {
      const client = await YogaApiClient.ClientFactory();
      await client.deleteRecord(uniqueId, recordTime);

      dispatch({
        type: RecordActionTypes.YOGA_FINISH_DELETE,
        payload: { unique_id: uniqueId, record_time: recordTime },
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

// ========================================
// HomeVisit関連アクション
// ========================================

export const resetHomeVisitFlags = () => ({
  type: RecordActionTypes.HOMEVISIT_RESET_FLAGS,
});

export const fetchHomeVisitRecords = (uniqueId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.HOMEVISIT_START_FETCH });

    try {
      const client = await HomeVisitApiClient.ClientFactory();
      const records = await client.getRecords(uniqueId).catch(handleNotFound);

      dispatch({
        type: RecordActionTypes.HOMEVISIT_FINISH_FETCH,
        payload: records,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const fetchHomeVisitRecord = (uniqueId: string, sequence: number) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.HOMEVISIT_START_FETCH_ONE });

    try {
      const client = await HomeVisitApiClient.ClientFactory();
      const record = await client.getRecord(uniqueId, sequence);

      dispatch({
        type: RecordActionTypes.HOMEVISIT_FINISH_FETCH_ONE,
        payload: record,
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({
          type: RecordActionTypes.HOMEVISIT_FINISH_FETCH_ONE,
          payload: null,
        });
      } else {
        dispatch({
          type: RecordActionTypes.API_ERROR,
          error,
        });
      }
    }
  };
};

export const createHomeVisitRecord = (uniqueId: string, data: Partial<HomeVisitRecord>) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.HOMEVISIT_START_CREATE });

    try {
      const client = await HomeVisitApiClient.ClientFactory();
      const record = await client.createRecord(uniqueId, data);

      dispatch({
        type: RecordActionTypes.HOMEVISIT_FINISH_CREATE,
        payload: record,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const updateHomeVisitRecord = (uniqueId: string, sequence: number, data: Partial<HomeVisitRecord>) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.HOMEVISIT_START_UPDATE });

    try {
      const client = await HomeVisitApiClient.ClientFactory();
      const record = await client.updateRecord(uniqueId, sequence, data);

      dispatch({
        type: RecordActionTypes.HOMEVISIT_FINISH_UPDATE,
        payload: record,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const deleteHomeVisitRecord = (uniqueId: string, sequence: number) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.HOMEVISIT_START_DELETE });

    try {
      const client = await HomeVisitApiClient.ClientFactory();
      await client.deleteRecord(uniqueId, sequence);

      dispatch({
        type: RecordActionTypes.HOMEVISIT_FINISH_DELETE,
        payload: { unique_id: uniqueId, sequence },
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

// ========================================
// Interest関連アクション
// ========================================

export const resetInterestFlags = () => ({
  type: RecordActionTypes.INTEREST_RESET_FLAGS,
});

export const fetchInterestRecords = (uniqueId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.INTEREST_START_FETCH });

    try {
      const client = await InterestApiClient.ClientFactory();
      const records = await client.getRecords(uniqueId).catch(handleNotFound);

      dispatch({
        type: RecordActionTypes.INTEREST_FINISH_FETCH,
        payload: records,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const fetchInterestRecord = (uniqueId: string, sequence: number) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.INTEREST_START_FETCH_ONE });

    try {
      const client = await InterestApiClient.ClientFactory();
      const record = await client.getRecord(uniqueId, sequence);

      dispatch({
        type: RecordActionTypes.INTEREST_FINISH_FETCH_ONE,
        payload: record,
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({
          type: RecordActionTypes.INTEREST_FINISH_FETCH_ONE,
          payload: null,
        });
      } else {
        dispatch({
          type: RecordActionTypes.API_ERROR,
          error,
        });
      }
    }
  };
};

export const createInterestRecord = (uniqueId: string, data: Partial<InterestCheckRecord>) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.INTEREST_START_CREATE });

    try {
      const client = await InterestApiClient.ClientFactory();
      const record = await client.createRecord(uniqueId, data);

      dispatch({
        type: RecordActionTypes.INTEREST_FINISH_CREATE,
        payload: record,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const updateInterestRecord = (uniqueId: string, sequence: number, data: Partial<InterestCheckRecord>) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.INTEREST_START_UPDATE });

    try {
      const client = await InterestApiClient.ClientFactory();
      const record = await client.updateRecord(uniqueId, sequence, data);

      dispatch({
        type: RecordActionTypes.INTEREST_FINISH_UPDATE,
        payload: record,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const deleteInterestRecord = (uniqueId: string, sequence: number) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.INTEREST_START_DELETE });

    try {
      const client = await InterestApiClient.ClientFactory();
      await client.deleteRecord(uniqueId, sequence);

      dispatch({
        type: RecordActionTypes.INTEREST_FINISH_DELETE,
        payload: { unique_id: uniqueId, sequence },
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

// ========================================
// MCI関連アクション
// ========================================

export const resetMCIFlags = () => ({
  type: RecordActionTypes.MCI_RESET_FLAGS,
});

export const fetchMCIRecords = (uniqueId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.MCI_START_FETCH });

    try {
      const client = await MCIApiClient.ClientFactory();
      const records = await client.getScores(uniqueId).catch(handleNotFound);

      dispatch({
        type: RecordActionTypes.MCI_FINISH_FETCH,
        payload: records,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const fetchMCIRecord = (uniqueId: string, timestamp: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.MCI_START_FETCH_ONE });

    try {
      const client = await MCIApiClient.ClientFactory();
      const record = await client.getScore(uniqueId, timestamp);

      dispatch({
        type: RecordActionTypes.MCI_FINISH_FETCH_ONE,
        payload: record,
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({
          type: RecordActionTypes.MCI_FINISH_FETCH_ONE,
          payload: null,
        });
      } else {
        dispatch({
          type: RecordActionTypes.API_ERROR,
          error,
        });
      }
    }
  };
};

export const createMCIRecord = (uniqueId: string, data: Partial<MCITestRecord>) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.MCI_START_CREATE });

    try {
      const client = await MCIApiClient.ClientFactory();
      const record = await client.createScore(uniqueId, data);

      dispatch({
        type: RecordActionTypes.MCI_FINISH_CREATE,
        payload: record,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const updateMCIRecord = (uniqueId: string, timestamp: string, data: Partial<MCITestRecord>) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.MCI_START_UPDATE });

    try {
      const client = await MCIApiClient.ClientFactory();
      const record = await client.updateScore(uniqueId, timestamp, data);

      dispatch({
        type: RecordActionTypes.MCI_FINISH_UPDATE,
        payload: record,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const deleteMCIRecord = (uniqueId: string, timestamp: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.MCI_START_DELETE });

    try {
      const client = await MCIApiClient.ClientFactory();
      await client.deleteScore(uniqueId, timestamp);

      dispatch({
        type: RecordActionTypes.MCI_FINISH_DELETE,
        payload: { unique_id: uniqueId, timestamp },
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

// ========================================
// LifeCheck関連アクション
// ========================================

export const resetLifeCheckFlags = () => ({
  type: RecordActionTypes.LIFECHECK_RESET_FLAGS,
});

export const fetchLifeCheckRecords = (uniqueId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.LIFECHECK_START_FETCH });

    try {
      const client = await LifeCheckApiClient.ClientFactory();
      const records = await client.getRecords(uniqueId).catch(handleNotFound);

      dispatch({
        type: RecordActionTypes.LIFECHECK_FINISH_FETCH,
        payload: records,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const fetchLifeCheckRecord = (uniqueId: string, sequence: number) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.LIFECHECK_START_FETCH_ONE });

    try {
      const client = await LifeCheckApiClient.ClientFactory();
      const record = await client.getRecord(uniqueId, sequence);

      dispatch({
        type: RecordActionTypes.LIFECHECK_FINISH_FETCH_ONE,
        payload: record,
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({
          type: RecordActionTypes.LIFECHECK_FINISH_FETCH_ONE,
          payload: null,
        });
      } else {
        dispatch({
          type: RecordActionTypes.API_ERROR,
          error,
        });
      }
    }
  };
};

export const createLifeCheckRecord = (uniqueId: string, data: Partial<LifeCheckRecord>) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.LIFECHECK_START_CREATE });

    try {
      const client = await LifeCheckApiClient.ClientFactory();
      const record = await client.createRecord(uniqueId, data);

      dispatch({
        type: RecordActionTypes.LIFECHECK_FINISH_CREATE,
        payload: record,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const updateLifeCheckRecord = (uniqueId: string, sequence: number, data: Partial<LifeCheckRecord>) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.LIFECHECK_START_UPDATE });

    try {
      const client = await LifeCheckApiClient.ClientFactory();
      const record = await client.updateRecord(uniqueId, sequence, data);

      dispatch({
        type: RecordActionTypes.LIFECHECK_FINISH_UPDATE,
        payload: record,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const deleteLifeCheckRecord = (uniqueId: string, sequence: number) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.LIFECHECK_START_DELETE });

    try {
      const client = await LifeCheckApiClient.ClientFactory();
      await client.deleteRecord(uniqueId, sequence);

      dispatch({
        type: RecordActionTypes.LIFECHECK_FINISH_DELETE,
        payload: { unique_id: uniqueId, sequence },
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

// ========================================
// Barthel関連アクション
// ========================================

export const resetBarthelFlags = () => ({
  type: RecordActionTypes.BARTHEL_RESET_FLAGS,
});

export const fetchBarthelRecords = (uniqueId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.BARTHEL_START_FETCH });

    try {
      const client = await BarthelApiClient.ClientFactory();
      // Barthelは全期間で取得
      const records = await client.getRecordsByDateRange(uniqueId, '2000-01-01', '2099-12-31').catch(handleNotFound);

      dispatch({
        type: RecordActionTypes.BARTHEL_FINISH_FETCH,
        payload: records,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const fetchBarthelRecord = (uniqueId: string, date: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.BARTHEL_START_FETCH_ONE });

    try {
      const client = await BarthelApiClient.ClientFactory();
      const record = await client.getRecord(uniqueId, date);

      dispatch({
        type: RecordActionTypes.BARTHEL_FINISH_FETCH_ONE,
        payload: record,
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({
          type: RecordActionTypes.BARTHEL_FINISH_FETCH_ONE,
          payload: null,
        });
      } else {
        dispatch({
          type: RecordActionTypes.API_ERROR,
          error,
        });
      }
    }
  };
};

export const createBarthelRecord = (uniqueId: string, data: Partial<BarthelRecord>) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.BARTHEL_START_CREATE });

    try {
      const client = await BarthelApiClient.ClientFactory();
      const record = await client.createRecord({ ...data, unique_id: uniqueId });

      dispatch({
        type: RecordActionTypes.BARTHEL_FINISH_CREATE,
        payload: record,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const updateBarthelRecord = (uniqueId: string, date: string, data: Partial<BarthelRecord>) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.BARTHEL_START_UPDATE });

    try {
      const client = await BarthelApiClient.ClientFactory();
      const record = await client.updateRecord(uniqueId, date, data);

      dispatch({
        type: RecordActionTypes.BARTHEL_FINISH_UPDATE,
        payload: record,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const deleteBarthelRecord = (uniqueId: string, date: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.BARTHEL_START_DELETE });

    try {
      const client = await BarthelApiClient.ClientFactory();
      await client.deleteRecord(uniqueId, date);

      dispatch({
        type: RecordActionTypes.BARTHEL_FINISH_DELETE,
        payload: { unique_id: uniqueId, record_time: date },
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

// ========================================
// MoffTraining関連アクション
// ========================================

export const resetMoffTrainingFlags = () => ({
  type: RecordActionTypes.MOFFTRAINING_RESET_FLAGS,
});

export const fetchMoffTrainingRecords = (userId: string, yearMonth: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.MOFFTRAINING_START_FETCH });

    try {
      const client = await TrainingLogApiClient.ClientFactory();

      // yearMonth (YYYY-MM) から from_date と to_date を計算
      const [year, month] = yearMonth.split('-').map(Number);
      const fromDate = `${year}-${String(month).padStart(2, '0')}-01`;
      // 月末日を計算
      const lastDay = new Date(year, month, 0).getDate();
      const toDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      // 新しいエンドポイントで個別レコードを取得
      const trainingLogs = await client.getTrainingLogs(userId, fromDate, toDate);

      // TrainingLog を MoffTrainingRecord にマッピング
      const records: MoffTrainingRecord[] = trainingLogs.map(log => ({
        user_id: log.user_id,
        training_id: log.training_id,
        started_at: log.started_at,
        ended_at: log.ended_at,
        count: log.count,
        average_angle: log.average_angle,
        average_duration: log.average_duration,
        initial_angle: log.initial_angle,
        target_angle: log.target_angle,
        angles: log.angles,
        absolute_angles: log.absolute_angles,
        durations: log.durations,
        dominant_hand: log.dominant_hand,
        with_band: log.with_band,
      }));

      dispatch({
        type: RecordActionTypes.MOFFTRAINING_FINISH_FETCH,
        payload: { records, menus: TRAINING_MENUS },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({
          type: RecordActionTypes.MOFFTRAINING_FINISH_FETCH,
          payload: { records: [], menus: TRAINING_MENUS },
        });
      } else {
        dispatch({
          type: RecordActionTypes.API_ERROR,
          error,
        });
      }
    }
  };
};

export const fetchMoffTrainingRecord = (userId: string, trainingId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.MOFFTRAINING_START_FETCH_ONE });

    try {
      const client = await TrainingLogApiClient.ClientFactory();
      const record = await client.getLatestTrainingLog(userId, trainingId);

      dispatch({
        type: RecordActionTypes.MOFFTRAINING_FINISH_FETCH_ONE,
        payload: record,
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({
          type: RecordActionTypes.MOFFTRAINING_FINISH_FETCH_ONE,
          payload: null,
        });
      } else {
        dispatch({
          type: RecordActionTypes.API_ERROR,
          error,
        });
      }
    }
  };
};

export const createMoffTrainingRecord = (
  userId: string,
  data: Partial<MoffTrainingRecord>,
  instructorName?: string,
) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.MOFFTRAINING_START_CREATE });

    try {
      const client = await TrainingLogApiClient.ClientFactory();
      const record = await client.createTrainingLog(
        userId,
        {
          training_id: data.training_id!,
          started_at: data.started_at!,
          ended_at: data.ended_at,
          count: data.count,
          average_angle: data.average_angle,
          average_duration: data.average_duration,
          initial_angle: data.initial_angle,
          target_angle: data.target_angle,
          angles: data.angles,
          absolute_angles: data.absolute_angles,
          durations: data.durations,
          dominant_hand: data.dominant_hand,
          with_band: data.with_band,
        },
        instructorName,
      );

      dispatch({
        type: RecordActionTypes.MOFFTRAINING_FINISH_CREATE,
        payload: record,
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const updateMoffTrainingRecord = (userId: string, startedAt: string, data: Partial<MoffTrainingRecord>) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.MOFFTRAINING_START_UPDATE });

    try {
      const client = await TrainingLogApiClient.ClientFactory();
      await client.updateTrainingLog(userId, startedAt, {
        count: data.count,
        average_angle: data.average_angle,
        average_duration: data.average_duration,
        ended_at: data.ended_at,
        with_band: data.with_band,
        initial_angle: data.initial_angle,
        target_angle: data.target_angle,
        angles: data.angles,
        absolute_angles: data.absolute_angles,
        durations: data.durations,
        dominant_hand: data.dominant_hand,
      });

      // 更新後のデータを返す（APIは204を返すため、リクエストデータをマージ）
      dispatch({
        type: RecordActionTypes.MOFFTRAINING_FINISH_UPDATE,
        payload: { user_id: userId, started_at: startedAt, ...data },
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};

export const deleteMoffTrainingRecord = (userId: string, startedAt: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: RecordActionTypes.MOFFTRAINING_START_DELETE });

    try {
      const client = await TrainingLogApiClient.ClientFactory();
      await client.deleteTrainingLog(userId, startedAt);

      dispatch({
        type: RecordActionTypes.MOFFTRAINING_FINISH_DELETE,
        payload: { user_id: userId, started_at: startedAt },
      });
    } catch (error) {
      dispatch({
        type: RecordActionTypes.API_ERROR,
        error,
      });
    }
  };
};
